import { useEffect, useRef, useState } from 'react';

import { type User } from 'models/user';
import { usersApi } from 'services/rtk/users';

export function useInfiniteUsers(search: string, teamId: number | undefined) {
  const [page, setPage] = useState(1);
  const [allUsers, setAllUsers] = useState<NonNullable<User['assignedUsers']>>([]);

  const { data, isFetching } = usersApi.useFetchUsersQuery(
    {
      page,
      search: search || undefined,
      limit: 15,
      statuses: ['ACTIVE'],
      teamId: teamId || undefined,
    },
    { refetchOnMountOrArgChange: true }
  );

  const hasMore = data?.pagination.page && data?.pagination.page < data?.pagination.pageCount;

  const lastSearch = useRef(search);
  const lastData = useRef<NonNullable<User['assignedUsers']> | null>(null);

  // сброс при смене поиска
  useEffect(() => {
    if (lastSearch.current !== search) {
      lastSearch.current = search;
      setPage(1);
    }
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [teamId]);

  useEffect(() => {
    if (data?.data && data.data !== lastData.current) {
      setAllUsers((prev) => (page === 1 ? data.data : [...prev, ...data.data]));
      lastData.current = data.data;
    }
  }, [data?.data, page]);

  const loadNext = () => {
    if (!hasMore || isFetching) return;
    setPage((prev) => prev + 1);
  };

  const removeFromSearched = (id: number) => {
    setAllUsers((users) => users.filter((user) => user.id !== id));
  };

  const addToSearched = (user: NonNullable<User['assignedUsers']>[number]) => {
    setAllUsers((users) => {
      const filteredFromCurrent = users.filter((u) => u.id !== user.id);
      return [user, ...filteredFromCurrent];
    });
  };

  return {
    searchedUsers: allUsers,
    loadNext,
    hasMore: hasMore ?? false,
    isUsersFetching: isFetching,
    removeFromSearched,
    addToSearched,
  };
}
